import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAccessToken } from '@/utils/jwt';

export interface SkillSummary {
  name: string;
  slug: string;
  description: string;
}

export interface SkillDetail {
  name: string;
  slug: string;
  description: string;
  version: string;
  body: string;
  references: string[];
}

export interface LintResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export const skillsApi = createApi({
  reducerPath: 'skillsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Skill'],
  endpoints: (builder) => ({
    listSkills: builder.query<SkillSummary[], undefined>({
      query: () => '/studio/skills',
      transformResponse: (resp: { success: boolean; skills: SkillSummary[] }) => resp.skills,
      providesTags: ['Skill'],
    }),
    getSkill: builder.query<SkillDetail, string>({
      query: (slug) => `/studio/skills/${slug}`,
      transformResponse: (resp: { success: boolean; skill: SkillDetail }) => resp.skill,
      providesTags: (_r, _e, slug) => [{ type: 'Skill', id: slug }],
    }),
    createSkill: builder.mutation<{ slug: string }, { slug: string; content: string }>({
      query: (body) => ({ url: '/studio/skills', method: 'POST', body }),
      invalidatesTags: ['Skill'],
    }),
    updateSkill: builder.mutation<{ slug: string }, { slug: string; content: string }>({
      query: ({ slug, content }) => ({
        url: `/studio/skills/${slug}`,
        method: 'PUT',
        body: { content },
      }),
      invalidatesTags: (_r, _e, { slug }) => [{ type: 'Skill', id: slug }, 'Skill'],
    }),
    deleteSkill: builder.mutation<{ success: boolean }, string>({
      query: (slug) => ({ url: `/studio/skills/${slug}`, method: 'DELETE' }),
      invalidatesTags: ['Skill'],
    }),
    lintSkill: builder.mutation<LintResult, string>({
      query: (slug) => ({ url: `/studio/skills/${slug}/lint`, method: 'POST' }),
    }),
  }),
});

export const {
  useListSkillsQuery,
  useGetSkillQuery,
  useCreateSkillMutation,
  useUpdateSkillMutation,
  useDeleteSkillMutation,
  useLintSkillMutation,
} = skillsApi;
